<template>
  <div class="app-landing">
    <!-- 全新的 Hero 部分：采用深色背景与科技感动态效果 -->
    <div class="hero-section">
      <!-- 动态渐变背景层 -->
      <div class="hero-bg-gradient"></div>
      <!-- 科技感网格与光晕效果 -->
      <div class="hero-bg-grid"></div>
      <div class="hero-bg-glow glow-1"></div>
      <div class="hero-bg-glow glow-2"></div>

      <div class="container">
        <div class="hero-content">
          <!-- 新增徽章标签，提升品牌科技感 -->
          <div class="hero-badge">🎮 PREMIUM GAME STUDIO</div>
          <h1 class="app-title">StickerMaker Studio</h1>
          <p class="hero-subtitle">Immerse yourself in an ocean of colors, <br class="mobile-break">and let your worries drift away.</p>
          <p class="hero-tagline">We create peaceful coloring games to help you unwind, relax, and find joy in every tap.</p>
          <div class="download-btn-wrapper">
            <a
                href="https://play.google.com/store/apps/developer?id=StickerMaker+Studio"
                class="download-btn"
                target="_blank"
                rel="noopener noreferrer"
            >
              <span class="btn-text">Explore Our Games</span>
              <span class="btn-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- About Us 部分：重新设计为类似 X-FLOW 的介绍布局 -->
    <div class="about-section">
      <div class="container">
        <div class="about-wrapper">
          <div class="about-content">
            <div class="section-label">ABOUT US</div>
            <h2 class="about-title">We craft digital sanctuaries <span class="text-gradient">for the world</span></h2>
            <p class="about-text">
              StickerMaker Studio was born from a simple belief: <strong>everyone deserves a moment of peace</strong>.
              In a world full of stress, notifications, and endless to-do lists, we wanted to create a tiny escape —
              a place where you can breathe, let go, and simply <strong>color your worries away</strong>.
            </p>
            <p class="about-text">
              Our team of artists and developers pours heart into every palette, every illustration, every gentle animation.
              We don't just make games; we craft <strong>digital sanctuaries</strong> — quiet corners filled with soft hues,
              magical creatures, and soothing patterns. No timers, no scores, no pressure. Just you and the joy of creation.
            </p>
            <div class="about-stats">
              <div class="stat-item">
                <span class="stat-number">2021</span>
                <span class="stat-label">Founded</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">1000000+</span>
                <span class="stat-label">Global Downloads</span>
              </div>
            </div>
          </div>
          <div class="about-image">
            <div class="about-image-inner">
              <div class="floating-emoji emoji-1">🎨</div>
              <div class="floating-emoji emoji-2">🖌️</div>
              <div class="floating-emoji emoji-3">🌈</div>
              <div class="floating-emoji emoji-4">✨</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Core Values 部分：重新设计为类似 X-FLOW 的游戏列表风格 -->
    <div class="values-section">
      <div class="container">
        <div class="section-header">
          <div class="section-label">CORE VALUES</div>
          <h2 class="section-title">Why we create</h2>
          <p class="section-subtitle">Because your mind deserves a peaceful break</p>
        </div>
        <div class="values-grid">
          <div class="value-card" v-for="value in coreValues" :key="value.id">
            <div class="value-icon">{{ value.icon }}</div>
            <h3>{{ value.title }}</h3>
            <p>{{ value.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Games Showcase 部分：采用类似 X-FLOW 的游戏卡片展示 -->
    <div class="games-section">
      <div class="container">
        <div class="section-header">
          <div class="section-label">OUR GAMES</div>
          <h2 class="section-title">Coloring worlds <span class="text-gradient">to explore</span></h2>
          <p class="section-subtitle">Step into a sanctuary of color and calm</p>
        </div>
        <div class="games-grid">
          <div
              v-for="product in products"
              :key="product.id"
              class="game-card"
          >
            <div class="game-card-inner">
              <div class="game-image" :style="{ background: product.bgColor }">
                <span class="game-emoji">{{ product.emoji }}</span>
              </div>
              <div class="game-info">
                <h3 class="game-name">{{ product.name }}</h3>
                <p class="game-desc">{{ product.description }}</p>
                <a :href="product.link" class="game-link" target="_blank" rel="noopener noreferrer">
                  Learn More
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA 部分：类似 X-FLOW 的联系方式区域 -->
    <div class="cta-section">
      <div class="container">
        <div class="cta-box">
          <h2>Ready to start <span class="text-gradient">your journey?</span></h2>
          <p>Join millions of players who find peace and joy in our coloring worlds.</p>
          <a href="https://play.google.com/store/apps/developer?id=StickerMaker+Studio" class="cta-btn" target="_blank">
            Download Now
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Footer 部分：深色风格，类似 X-FLOW -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-brand">
            <h3>StickerMaker Studio</h3>
            <p>Creating peaceful coloring experiences for a calmer world.</p>
          </div>
          <div class="footer-links">
            <div class="footer-links-column">
              <h4>Studio</h4>
              <a href="#">About Us</a>
              <a href="#">Our Games</a>
            </div>
            <div class="footer-links-column">
              <h4>Support</h4>
              <a href="PRIVACY_POLICY.html">Privacy Policy</a>
              <a href="TERMS_OF_SERVICE.html">Terms of Service</a>
            </div>
            <div class="footer-links-column">
              <h4>Connect</h4>
              <a href="#jvjvnee@gmail.com">jvjvnee@gmail.com</a>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <p>© 2025 StickerMaker Studio. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'CompanyLanding',
  data() {
    return {
      coreValues: [
        {
          id: 1,
          icon: '🌊',
          title: 'Immerse in Color',
          description: 'Let vibrant hues wash over you, washing away stress and leaving only calm.'
        },
        {
          id: 2,
          icon: '🧘',
          title: 'Let Go of Worries',
          description: 'Our games are designed to help you disconnect from daily chaos and reconnect with yourself.'
        },
        {
          id: 3,
          icon: '🖌️',
          title: 'Mindful Creation',
          description: 'Every stroke is a meditation. No rush, no competition — just pure artistic flow.'
        },
        {
          id: 4,
          icon: '💖',
          title: 'Made with Love',
          description: 'Each illustration is handcrafted to spark joy and soothe your soul.'
        }
      ],
      products: [
        {
          id: 1,
          name: 'Sweet Color',
          description: 'Discover the ultimate coloring experience with Sweet Color',
          emoji: '😍',
          bgColor: 'linear-gradient(135deg, #FFE4B5, #FFD5A4)',
          link: 'https://play.google.com/store/apps/details?id=com.sweet.coloring.book.paint.by.number.puzzle.game'
        },
        {
          id: 2,
          name: 'Haven Color',
          description: 'Intricate mandalas that bring deep focus and relaxation.',
          emoji: '🧘‍♀️',
          bgColor: 'linear-(135deg, #E6E6FA, #D8BFD8)',
          link: 'https://play.google.com/store/apps/details?id=com.haven.color.happy.paint.number.art.calm'
        },
        {
          id: 3,
          name: 'Personal Stickers',
          description: 'Millions of funny animated stickers for WhatsApp',
          emoji: '😂',
          bgColor: 'linear-gradient(135deg, #C9E4DE, #B5D3CC)',
          link: 'https://play.google.com/store/apps/details?id=com.whatsapp.space.packs'
        }
      ]
    };
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app-landing {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  background: #0a0a0f;
  overflow-x: hidden;
  color: #ffffff;
}

.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Typography */
.text-gradient {
  background: linear-gradient(135deg, #e8a87c, #ffd5a4);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.section-label {
  font-size: 0.85rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #e8a87c;
  margin-bottom: 16px;
  font-weight: 600;
}

.section-header {
  text-align: center;
  margin-bottom: 64px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #ffffff;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #a0a0b0;
  max-width: 600px;
  margin: 0 auto;
}

/* Hero Section - 深色科技感风格 */
.hero-section {
  position: relative;
  overflow: hidden;
  padding: 120px 0 140px;
  min-height: 90vh;
  display: flex;
  align-items: center;
}

.hero-bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 30% 50%, #1a1a2e, #0a0a0f);
  z-index: 0;
}

.hero-bg-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
      linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  z-index: 1;
  pointer-events: none;
}

.hero-bg-glow {
  position: absolute;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.3;
  z-index: 1;
  pointer-events: none;
}

.glow-1 {
  background: #e8a87c;
  top: -200px;
  left: -200px;
  animation: floatGlow 12s infinite alternate;
}

.glow-2 {
  background: #c48b5e;
  bottom: -200px;
  right: -200px;
  animation: floatGlow 15s infinite alternate-reverse;
}

@keyframes floatGlow {
  0% { transform: translate(0, 0) scale(1); opacity: 0.2; }
  100% { transform: translate(50px, 50px) scale(1.2); opacity: 0.4; }
}

.hero-content {
  position: relative;
  z-index: 10;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.hero-badge {
  display: inline-block;
  background: rgba(232, 168, 124, 0.15);
  backdrop-filter: blur(10px);
  padding: 8px 20px;
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1px;
  color: #e8a87c;
  margin-bottom: 24px;
  border: 1px solid rgba(232, 168, 124, 0.3);
}

.app-title {
  font-size: 4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ffffff, #e8a87c);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  margin-bottom: 24px;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 1.4rem;
  color: #c0c0d0;
  margin-bottom: 20px;
  line-height: 1.4;
}

.hero-tagline {
  font-size: 1rem;
  color: #9090a0;
  max-width: 500px;
  margin: 0 auto 32px;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #e8a87c, #c48b5e);
  padding: 16px 40px;
  border-radius: 100px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  color: white;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.download-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(232, 168, 124, 0.3);
  gap: 16px;
}

.btn-icon svg {
  transition: transform 0.2s;
}

.download-btn:hover .btn-icon svg {
  transform: translateX(4px);
}

/* About Section */
.about-section {
  padding: 100px 0;
  background: #0f0f15;
  border-top: 1px solid rgba(255,255,255,0.05);
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.about-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.about-title {
  font-size: 2.8rem;
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.2;
}

.about-text {
  color: #a0a0b0;
  line-height: 1.7;
  margin-bottom: 24px;
  font-size: 1rem;
}

.about-stats {
  display: flex;
  gap: 40px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.stat-item {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #e8a87c;
}

.stat-label {
  font-size: 0.85rem;
  color: #707080;
  margin-top: 4px;
}

.about-image {
  position: relative;
  height: 400px;
}

.about-image-inner {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(232,168,124,0.1), rgba(196,139,94,0.05));
  border-radius: 40px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.floating-emoji {
  position: absolute;
  font-size: 3rem;
  animation: float 6s infinite ease-in-out;
}

.emoji-1 { top: 15%; left: 20%; animation-delay: 0s; }
.emoji-2 { bottom: 20%; right: 25%; animation-delay: 1s; }
.emoji-3 { top: 40%; right: 15%; animation-delay: 2s; }
.emoji-4 { bottom: 30%; left: 20%; animation-delay: 3s; }

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

/* Values Section */
.values-section {
  padding: 100px 0;
  background: #0a0a0f;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 32px;
}

.value-card {
  background: rgba(255,255,255,0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 24px;
  padding: 32px 24px;
  text-align: center;
  transition: all 0.3s ease;
}

.value-card:hover {
  transform: translateY(-8px);
  border-color: rgba(232,168,124,0.3);
  background: rgba(232,168,124,0.05);
}

.value-icon {
  font-size: 3rem;
  margin-bottom: 20px;
}

.value-card h3 {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #ffffff;
}

.value-card p {
  color: #a0a0b0;
  line-height: 1.6;
  font-size: 0.9rem;
}

/* Games Section */
.games-section {
  padding: 100px 0;
  background: #0f0f15;
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
}

.game-card {
  background: rgba(255,255,255,0.03);
  border-radius: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(255,255,255,0.05);
}

.game-card:hover {
  transform: translateY(-8px);
  border-color: rgba(232,168,124,0.3);
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
}

.game-card-inner {
  padding: 24px;
}

.game-image {
  height: 160px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.game-emoji {
  font-size: 4rem;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.2));
}

.game-name {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #ffffff;
}

.game-desc {
  font-size: 0.85rem;
  color: #a0a0b0;
  line-height: 1.5;
  margin-bottom: 16px;
}

.game-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #e8a87c;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  transition: gap 0.2s;
}

.game-link:hover {
  gap: 12px;
}

.game-link svg {
  transition: transform 0.2s;
}

.game-link:hover svg {
  transform: translateX(3px);
}

/* CTA Section */
.cta-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #0a0a0f, #0f0f15);
}

.cta-box {
  text-align: center;
  background: rgba(232,168,124,0.05);
  border: 1px solid rgba(232,168,124,0.2);
  border-radius: 48px;
  padding: 64px 48px;
  backdrop-filter: blur(20px);
}

.cta-box h2 {
  font-size: 2.5rem;
  margin-bottom: 16px;
}

.cta-box p {
  color: #a0a0b0;
  margin-bottom: 32px;
  font-size: 1rem;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #e8a87c, #c48b5e);
  padding: 14px 36px;
  border-radius: 100px;
  text-decoration: none;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
}

.cta-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(232,168,124,0.3);
  gap: 16px;
}

/* Footer */
.footer {
  background: #060608;
  padding: 60px 0 30px;
  border-top: 1px solid rgba(255,255,255,0.05);
}

.footer-content {
  display: grid;
  grid-template-columns: 1.5fr 2fr;
  gap: 60px;
  margin-bottom: 40px;
}

.footer-brand h3 {
  font-size: 1.5rem;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #ffffff, #e8a87c);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.footer-brand p {
  color: #707080;
  font-size: 0.85rem;
  line-height: 1.5;
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
}

.footer-links-column h4 {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #ffffff;
}

.footer-links-column a {
  display: block;
  color: #707080;
  text-decoration: none;
  font-size: 0.85rem;
  margin-bottom: 12px;
  transition: color 0.2s;
}

.footer-links-column a:hover {
  color: #e8a87c;
}

.footer-bottom {
  text-align: center;
  padding-top: 30px;
  border-top: 1px solid rgba(255,255,255,0.05);
  color: #606070;
  font-size: 0.8rem;
}

/* Responsive */
@media (max-width: 968px) {
  .about-wrapper {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .about-image {
    height: 300px;
  }

  .footer-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .app-title {
    font-size: 2.8rem;
  }

  .hero-subtitle {
    font-size: 1.2rem;
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 80px 0 100px;
  }

  .app-title {
    font-size: 2.2rem;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .about-title {
    font-size: 2rem;
  }

  .values-grid {
    grid-template-columns: 1fr;
  }

  .games-grid {
    grid-template-columns: 1fr;
  }

  .cta-box {
    padding: 40px 24px;
  }

  .cta-box h2 {
    font-size: 1.8rem;
  }

  .footer-links {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 20px;
  }

  .about-stats {
    flex-wrap: wrap;
    gap: 20px;
  }

  .footer-links {
    grid-template-columns: 1fr;
  }

  .mobile-break {
    display: block;
  }
}
</style>